/**
 * ErrorBoundary — catches unhandled render errors and shows a fallback UI.
 */

import { Component, type ReactNode, type ErrorInfo } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(
      "[ErrorBoundary] Uncaught error:",
      error,
      info.componentStack,
    );

    // Log render error to server
    this.logRenderError(error, info.componentStack);
  }

  private async logRenderError(
    error: Error,
    componentStack: string,
  ): Promise<void> {
    try {
      // Dynamic import to avoid circular dependencies
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const admin = await import("../api/admin-errors.js").catch(() => null);
      if (admin?.logCriticalError) {
        await admin.logCriticalError(
          "RENDER_ERROR",
          error.message,
          {
            surface: "frontend",
            errorBoundary: true,
            componentStack,
            page: window.location.pathname,
            page_url: window.location.href,
            is_blocking: true,
          },
          error.stack,
        );
      }
    } catch {
      // Silently fail - never break the app due to error logging
    }
  }

  private handleRetry = (): void => {
    if (this.state.retryCount >= 2) {
      // After 2 retries, redirect to dashboard to break the loop
      window.location.href = "/app";
      return;
    }
    this.setState((prev) => ({
      hasError: false,
      error: null,
      retryCount: prev.retryCount + 1,
    }));
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback != null) {
        return this.props.fallback;
      }

      // Check if this looks like an auth error
      const errMsg = this.state.error?.message?.toLowerCase() ?? "";
      const isAuthError =
        errMsg.includes("401") ||
        errMsg.includes("unauthorized") ||
        errMsg.includes("session expired");

      if (isAuthError) {
        return (
          <div className="error-page">
            <h1>Session expired</h1>
            <p>
              Your session has expired. Please sign in again to continue.
            </p>
            <a href="/sign-in" className="btn btn--primary">
              Sign in
            </a>
          </div>
        );
      }

      return (
        <div className="error-page">
          <h1>Something went wrong</h1>
          <p>
            Try refreshing the page. If the problem persists, please contact
            support.
          </p>
          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              className="btn btn--primary"
              onClick={this.handleRetry}
            >
              {this.state.retryCount >= 2 ? "Go to dashboard" : "Try again"}
            </button>
            <a href="/app" className="btn btn--secondary">
              Go to dashboard
            </a>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
